import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handleApiError } from "../api/instanceApi";
import { toast } from "react-toastify";
import { role } from "../constants/userConstants";
import { all_routes } from "../../feature-module/router/all_routes";
import { useNavigate } from "react-router-dom";
import {
  getSiteInfo,
  loginToMoodle,
  getUserInfo,
  logOut,
  checkForcePassChage,
  changePassword,
  updateUser,
  updateUserPicture,
} from "../api/moodleUserApi";
import { getUserRole } from "../api/moodleUserApi";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { resetPassword } from "../api/moodleForgotPassword";


export const useAuth = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  // Lấy thông tin user từ cache (nếu có)

  const {
    data: moodleUser,
    isError: isMoodleUserError,
    isLoading: isMoodleUserLoading,
  } = useQuery({
    queryKey: ["moodle-user"],
    queryFn: async () => {
      const siteInfo = await getSiteInfo();
      if (siteInfo?.userid) {
        const userInfo = await getUserInfo(siteInfo.userid);
        const userRole = await getUserRole(siteInfo.userid);
        const userFullInfo = {
          ...userInfo[0],
          role: userRole?.[0],
        };
        return userFullInfo;
      }
      return null;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Không gọi lại khi component được mount
    staleTime: 60000,
  });
  const navigate = useNavigate();
  const moodleLoginMutation = useMutation({

    mutationFn: async (data: any) => {
      return await toast.promise(loginToMoodle(data), {
        pending: t('common.loggingIn') as string,
        success: {
          render({ data }: any) {
            return t('common.loginSuccess') as string;
          },
        },
        error: {
          render({ data }: any) {
            if (typeof data === 'string' && /<html|<body|<div/.test(data)) {
              return t('common.loginFailed') as string;
            }
            return (data?.message || t('common.loginFailed')) as string;
          },
        },
      });
    },
    onSuccess: async (data) => {
      if (!data?.token) {
        throw new Error(t('common.noToken'));
      }

      sessionStorage.removeItem("isForcePassChange");

      const route = all_routes;
      const { token } = data; sessionStorage.setItem("wstoken", token);
      const { privatetoken } = data; sessionStorage.setItem("privatetoken", privatetoken);
      sessionStorage.setItem('wstoken', token)
      sessionStorage.setItem('privatetoken', privatetoken)

      //Check đổi pass 
      const forceChangeResponse = await checkForcePassChage();
      if (forceChangeResponse?.required) {
        sessionStorage.setItem("isForcePassChange", "true");
        window.dispatchEvent(new CustomEvent("FORCE_PASSWORD_CHANGE"));
        // sessionStorage.removeItem('wstoken');
        return;
      }

      // Lấy thông tin site để có userid
      const siteInfo = await getSiteInfo();
      // Lấy role của user
      const userRole = await getUserRole(siteInfo.userid);

      queryClient.invalidateQueries({ queryKey: ["moodle-user"] });

      if (
        !userRole ||
        userRole.length === 0 ||
        userRole[0].roleid === role.student
      ) {
        navigate(route.studentDashboard);
      } else {
        window.location.href = `${process.env.REACT_APP_MOODLE_API}/local/react_sso/login.php?token=${data?.token}`;
      }
    },
    onError: handleApiError,
  });


  const logoutMutation = useMutation({
    mutationFn: async () => {
      return await toast.promise(logOut(), {
        pending: t('common.loggingOut') as string,
        success: t('common.logoutSuccess') as string,
      });
    },
    onSuccess: () => {
      const route = all_routes;
      sessionStorage.removeItem("token");
      queryClient.setQueryData(["user"], null);
      navigate(route.login);
    },
    onError: handleApiError,
  });

  return {
    moodleUser,
    isMoodleUserError,
    isMoodleUserLoading,
    moodleLogin: moodleLoginMutation.mutate,
    logout: logoutMutation.mutate,
    isAuthLoading:
      moodleLoginMutation.isPending || moodleLoginMutation.isPending,
  };
};

export const useChangePassword = (onSuccessCallback?: () => void) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (formData: any) => {
      const res = await changePassword(formData);
      // Nếu backend trả về dạng lỗi Moodle (moodle_exception, errorcode, message, debuginfo)
      if (res && typeof res === "object" && (res.errorcode || res.exception)) {
        // Map thông điệp thân thiện cho một số lỗi phổ biến
        const rawMessage = `${res?.message || ''}`.toLowerCase();
        const errorCode = `${res?.errorcode || ''}`.toLowerCase();
        const isInvalidOldPassword =
          errorCode.includes('invalidoldpassword') || rawMessage.includes('invalidoldpassword');

        if (isInvalidOldPassword) {
          throw new Error((t('common.invalidOldPassword') as string) || 'Mật khẩu cũ không đúng');
        }

        // Mặc định báo lỗi đổi mật khẩu thất bại
        throw new Error((t('common.changePasswordFailed') as string) || 'Đổi mật khẩu thất bại');
      }
      return res;
    },
    onSuccess: (res) => {
      toast.success(t('common.changePasswordSuccess') as string);
      if (onSuccessCallback) onSuccessCallback();
      sessionStorage.removeItem("isForcePassChange");
    },
    onError: (err: any) => {
      // Hiển thị thông báo thất bại (ưu tiên message thân thiện đã map phía trên)
      const fallback = t('common.changePasswordFailed') as string;
      const msg = (typeof err?.message === 'string' && err.message.trim()) ? err.message : fallback;
      toast.error(msg);
    },
  });
};

export const useUpdateInfo = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (formData: any) => {
      const res = await updateUser(formData);

      // Kiểm tra nếu là lỗi từ Moodle
      if (res && typeof res === 'object' && res.errorcode) {
        throw new Error(res.message || 'Đã có lỗi xảy ra'); // ném lỗi để vào onError
      }

      return res; // null hoặc kết quả hợp lệ
    },
    onSuccess: (res) => {
      // Nếu res là null (thành công logic theo yêu cầu)
      if (res === null) {
        toast.success(t('profile.updateInfoSuccess') as string);
      } else {
        // Có thể mở rộng logic xử lý kết quả hợp lệ khác (nếu cần)
        toast.success(t('profile.updateInfoSuccess') as string);
      }
    },
    onError: (err: any) => {
      // Có thể hiển thị lỗi cụ thể từ err.message
      toast.error(t('profile.updateInfoError') as string);
    },
  });
};

export const useUpdateUserPicture = (onSuccessCallback?: () => void) => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (formData: any) => {
      const res = await updateUserPicture(formData);

      if (res && typeof res === 'object' && res.errorcode) {
        throw new Error(res.message || t("common.updateFailed"));
      }

      return res;
    },
    onSuccess: (res) => {
      toast.success((t("common.updatePictureSuccess") as string));
      // if (onSuccessCallback) onSuccessCallback();
    },
    onError: (err: any) => {
      toast.error(err.message || t("common.updatePictureFailed"));
    },
  });
};

export const useResetPassword = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: resetPasswordData, isLoading, isError } = useQuery({
    queryKey: ["reset-password"],
    queryFn: async () => { },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 60000,
    enabled: false,
  });

  const checkUserExistByUsername = useMutation({
    mutationFn: async (username: string) => {
      return await resetPassword({
        action: "check_user",
        username: username,
      });
    },
    onSuccess: (res) => {
      queryClient.setQueryData(["reset-password"], res);
    },
  });

  const checkUserExistByEmail = useMutation({
    mutationFn: async (email: string) => {
      return await resetPassword({
        action: "check_user",
        email: email,
      });
    },
    onSuccess: (res) => {
      queryClient.setQueryData(["reset-password"], res);
    },
  });

  const clearResetPasswordData = () => {
    queryClient.setQueryData(["reset-password"], null);
  };

  const clearTokenValidation = () => {
    queryClient.setQueryData(["reset-password"], (old: unknown) => {
      if (!old || typeof old !== "object") return old;
      const { valid, user, message, ...rest } = old as Record<string, unknown>;
      return rest;
    });
  };

  const requestForgotPasswordByUsername = useMutation({
    mutationFn: async (username: string) => {
      return await resetPassword({
        action: "request_reset",
        username,
      });
    },
    onSuccess: (res) => {
      queryClient.setQueryData(["reset-password"], (old: unknown) => ({
        ...(typeof old === "object" && old !== null ? old : {}),
        ...res,
      }));
    },
  });

  const requestForgotPasswordByEmail = useMutation({
    mutationFn: async (email: string) => {
      return await resetPassword({
        action: "request_reset",
        email,
      });
    },
    onSuccess: (res) => {
      queryClient.setQueryData(["reset-password"], (old: unknown) => ({
        ...(typeof old === "object" && old !== null ? old : {}),
        ...res,
      }));
    },
  });

  const checkValidToken = useMutation({
    mutationFn: async (token: string) => {
      return await resetPassword({
        action: "validate_token",
        token: token,
      });
    },
    onSuccess: (res) => {
      queryClient.setQueryData(["reset-password"], (old: unknown) => ({
        ...(typeof old === "object" && old !== null ? old : {}),
        ...res,
      }));
    },
  });

  const confirmResetPassword = useMutation({
    mutationFn: async (data: { token: string; password: string }) => {
      return await resetPassword({
        action: "set_password",
        token: data.token,
        password: data.password,
      });
    },
    onSuccess: (res) => {
      queryClient.setQueryData(["reset-password"], (old: unknown) => ({
        ...(typeof old === "object" && old !== null ? old : {}),
        ...res,
      }));
      toast.success("Thay đổi mật khẩu thành công");
    },
    onError: (err: any) => {
      toast.error("Thay đổi mật khẩu thất bại, vui lòng thử lại!");
    },
  });

  return {
    checkUserExistByUsername: checkUserExistByUsername.mutate,
    checkUserExistByEmail: checkUserExistByEmail.mutate,
    resetPasswordData,
    clearResetPasswordData,
    clearTokenValidation,
    requestForgotPasswordByUsername: requestForgotPasswordByUsername.mutate,
    requestForgotPasswordByEmail: requestForgotPasswordByEmail.mutate,
    checkValidToken: checkValidToken.mutate,
    confirmResetPassword: confirmResetPassword.mutate,
    isCheckingUser:
      checkUserExistByUsername.isPending || checkUserExistByEmail.isPending,
    isRequestingForgotPassword:
      requestForgotPasswordByUsername.isPending ||
      requestForgotPasswordByEmail.isPending,
    isCheckingValidToken: checkValidToken.isPending,
    isConfirmingResetPassword: confirmResetPassword.isPending,
    isLoading,
    isError,
  };
};
